import type { IContainer, IPhoto, IPhotoCrop } from '../../common/types/index.js';
import { isEmpty } from '../../common/utils/index.js';
import { fitCropToContainer } from '../utils/fitCropToContainer.js';

function createPhotoCrop(cropArray: number[]): IPhotoCrop {
    const [x, y, width, height, rank] = cropArray;

    return {
        x,
        y,
        width,
        height,
        rank,
    };
}

function cropToPixels(crop: IPhotoCrop, photo: IPhoto): IPhotoCrop {
    const photoAspectRatio = photo.width / photo.height;
    const cropWidthPx = Math.round(crop.width * photo.width);
    const cropHeightPx = Math.round(cropWidthPx / photoAspectRatio);

    return {
        x: Math.round(crop.x * photo.width),
        y: Math.round(crop.y * photo.height),
        width: cropWidthPx,
        height: cropHeightPx,
        rank: crop.rank,
    };
}

function isCropOutOfBound(crop: IPhotoCrop, photo: IPhoto): boolean {
    return crop.x + crop.width > photo.width || crop.y + crop.height > photo.height;
}

export function getSuggestedCrop(photo: IPhoto, container: IContainer): IPhotoCrop | undefined {
    if (!photo.crops || isEmpty(photo.crops)) {
        return;
    }

    const containerAspectRatio = container.width / container.height;

    const cropSuggestions = Object.values(photo.crops)
        .map((cropArray) => createPhotoCrop(cropArray))
        .map((crop) => cropToPixels(crop, photo))
        .sort((a, b) => b.rank - a.rank);

    const bestIndex = cropSuggestions.reduce((bestIdx, crop, idx, crops) => {
        const aspectRatio = crop.width / crop.height;
        const aspectRatioDiff = Math.abs(containerAspectRatio - aspectRatio);

        const bestAspectRatio = crops[bestIdx].width / crops[bestIdx].height;
        const bestAspectRatioDiff = Math.abs(containerAspectRatio - bestAspectRatio);

        return bestAspectRatioDiff > aspectRatioDiff ? idx : bestIdx;
    }, 0);

    const maxIndex = cropSuggestions.length - 1;

    const bestCrop = bestIndex < maxIndex ? cropSuggestions[bestIndex] : undefined;

    if (!bestCrop || isCropOutOfBound(bestCrop, photo)) {
        return undefined;
    }

    return fitCropToContainer(bestCrop, container, photo);
}
